import React, { useEffect, useState } from "react"
import AldooStoreAPI from "aldoo-ra/Store/api"
import { store } from "aldoo-ra/Store"
import {
  CreditCardIcon,
  BuildingLibraryIcon,
  PlusIcon,
  XMarkIcon,
  CheckIcon,
  ExclamationCircleIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import LoadingOverlay from "aldoo-ra/LoadingOverlay"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import useLogout from "aldoo-ra/PlatformAuth/actions/logout"

// Payment method type icons and branding
const CARD_BRAND_ICONS = {
  visa: CreditCardIcon,
  mastercard: CreditCardIcon,
  amex: CreditCardIcon,
  discover: CreditCardIcon,
  default: CreditCardIcon,
}

const PaymentMethodIcon = ({ method, className = "w-7 h-7 text-gray_2" }) => {
  switch (method.type) {
    case "card": {
      const IconComponent =
        CARD_BRAND_ICONS[method.card.brand] || CARD_BRAND_ICONS.default
      return <IconComponent className={className} />
    }
    case "us_bank_account":
    case "sepa_debit":
    case "bacs_debit":
    case "au_becs_debit":
      return <BuildingLibraryIcon className={className} />
    default:
      return <CreditCardIcon className={className} />
  }
}

// Individual payment method card component
const PaymentMethodCard = ({
  method,
  defaultPaymentMethodId,
  onSetDefault,
  onDelete,
  texts,
}) => {
  const [loading, setLoading] = useState(false)
  const isDefault = method.id === defaultPaymentMethodId

  const handleSetDefault = async () => {
    if (loading || isDefault) return
    setLoading(true)
    try {
      await onSetDefault(method.id)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async () => {
    if (loading || isDefault) return
    setLoading(true)
    try {
      await onDelete(method.id)
    } finally {
      setLoading(false)
    }
  }

  const getDisplayInfo = () => {
    if (method.type === "card") {
      return {
        title: `${method.card.brand.toUpperCase()} •••• ${method.card.last4}`,
        subtitle: `${texts?.expiration_text || "Expires"} ${method.card.exp_month}/${method.card.exp_year}`,
      }
    }
    if (method.type === "link") {
      return {
        title: "Link",
        subtitle: method.link?.email || "Payment method",
      }
    }
    return {
      title: method.type.replace("_", " ").toUpperCase(),
      subtitle: "Payment method",
    }
  }

  const { title, subtitle } = getDisplayInfo()

  return (
    <div
      className={`w-full max-w-[450px] md:max-w-full flex flex-col md:flex-row items-center md:items-start justify-between gap-6 bg-theme_white rounded-[24px] p-8 md:px-10 md:py-9 mx-auto md:mx-0 ${
        isDefault ? "shadow-custom_card" : "shadow-md"
      }`}
    >
      {/* Payment method */}
      <div className="flex items-start space-x-3">
        <PaymentMethodIcon method={method}/>

        <div>
          <Typography
            variant="body_20_accent"
            className="text-text my-0">
            {title}
          </Typography>

          <Typography
            variant="info_16"
            className="text-gray_1 my-0">
            {subtitle}
          </Typography>
        </div>
      </div>

      {/* Default payment method label */}
      <div className="flex items-center space-x-2">
        {isDefault ? (
          <div className="w-auto h-[40px] md:h-[44px] lg:h-[48px] flex items-center justify-center space-x-2 text-text bg-primary_lt/70 rounded-full px-[16px] md:px-[20px] xl:px-[24px] py-3">
            <CheckIcon className="w-5 h-5" />

            <Typography
              variant="body_20"
              className="my-0"
            >
              {texts?.default_method || "Default Payment Method"}
            </Typography>
          </div>
        ) : (
          <div className="flex flex-col md:flex-row gap-2">
            {/* Set as Default btn */}
            <Button
              variant="secondary_s"
              onClick={handleSetDefault}
              disabled={loading}
              className="gap-2 disabled:opacity-50"
            >
              {loading ? (
                <div className="w-4 h-4 border-b-2 border-primary rounded-full animate-spin"></div>
              ) : (
                <ArrowPathIcon className="w-4 h-4" />
              )}
              {texts?.cta_set_default || "Set as Default"}
            </Button>

            {/* Delete btn */}
            <Button
              variant="error_s"
              onClick={handleDelete}
              disabled={loading}
            >
              {loading ? (
                <div className="w-4 h-4 border-b-2 border-error rounded-full animate-spin"></div>
              ) : (
                <div className="flex items-center gap-1">
                  <XMarkIcon className="w-5 h-5" />
                  {texts?.cta_remove || "Remove"}
                </div>
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}

const UserPaymentMethods = () => {
  const [paymentMethods, setPaymentMethods] = useState([])
  const [defaultPaymentMethodId, setDefaultPaymentMethodId] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showAddPayment, setShowAddPayment] = useState(false)
  const [overlayLoading, setOverlayLoading] = useState(false)
  const { getElement } = store

  const logout = useLogout()
  const AddPaymentMethod = getElement("add-payment-method")

  const texts = TextBundle({ id: "payment_methods" })

  const fetchPaymentMethods = async () => {
    try {
      const response = await AldooStoreAPI.getPaymentMethods()

      // Ensure response is always an array
      const methods = Array.isArray(response) ? response : []
      setPaymentMethods(methods)

      // Set default payment method ID from the response array
      if (methods.length > 0) {
        const defaultMethod = methods.find((method) => method.isDefault)
        setDefaultPaymentMethodId(defaultMethod?.id || null)
      }
    } catch (err) {
      // Check if error is due to invalid credentials/authentication
      if (err.message?.includes('401') || err.message?.includes('403') ||
          err.message?.includes('Unauthorized') || err.message?.includes('Forbidden')) {
        // User credentials are invalid, could be deleted account
        console.log('Payment methods fetch failed due to auth error, logging out user:', err.message)

        // Reset to safe state
        setPaymentMethods([])
        setDefaultPaymentMethodId(null)

        // Trigger logout to clear invalid session
        try {
          await logout()
        } catch (logoutErr) {
          console.error('Failed to logout after auth error:', logoutErr)
        }
      } else {
        setError(err.message)
        // Still ensure safe array state even on other errors
        setPaymentMethods([])
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchPaymentMethods()

    const handleUpdate = () => fetchPaymentMethods()
    window.addEventListener("payment-methods-updated", handleUpdate)
    return () =>
      window.removeEventListener("payment-methods-updated", handleUpdate)
  }, [])

  const handleSetDefault = async (payment_method_id) => {
    try {
      setOverlayLoading(true)
      await AldooStoreAPI.attachPaymentMethod({
        payment_method_id,
        makeDefault: true,
      })
      await fetchPaymentMethods()
    } catch (err) {
      console.error(texts?.default_error || "Error setting default payment method:", err)
    } finally {
      setOverlayLoading(false) // Correct placement in finally block
    }
  }

  const handleDelete = async (paymentMethodId) => {
    try {
      showPromptDialog({
        title: texts?.delete_dialog_title || "Delete Payment Method",
        message: texts?.delete_dialog_message || "Are you sure you want to delete this payment method?",
        confirmText: texts?.cta_delete_confirm || "Delete",
        onConfirm: async () => {
          setOverlayLoading(true)
          await AldooStoreAPI.detachPaymentMethod(paymentMethodId)
          await fetchPaymentMethods()
          setOverlayLoading(false)
        },
      })
    } catch (err) {
      console.error(texts?.delete_payment_error || "Error deleting payment method", err)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-4 rounded-[24px] bg-error/10 text-error flex items-center space-x-2">
        <ExclamationCircleIcon className="w-5 h-5" />
        <span>{texts?.error || "Error:"} {error}</span>
      </div>
    )
  }

  return (
    <div className="pt-2 lg:pt-4">
      {/* Only show top Add Payment Method button if there are existing payment methods */}
      {Array.isArray(paymentMethods) && paymentMethods.length > 0 && (
        <div className="flex justify-center md:justify-end mb-10">
          {/* Add a Payment Method button */}
          <Button
            variant="primary_s"
            onClick={() => setShowAddPayment(true)}
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            {texts?.cta_add || "Add Payment Method"}
          </Button>
        </div>
      )}

      {!Array.isArray(paymentMethods) || paymentMethods.length === 0 ? (
        <div className="flex flex-col items-center text-center py-9 px-4 rounded-[24px] border-2 border-dashed border-gray_3">
          <CreditCardIcon className="w-12 h-12 mx-auto text-gray_2 mb-3" />

          <Typography
            variant="info_18"
            className="text-text mt-0"
          >
            {texts?.no_payment_method || "No payment methods available"}
          </Typography>

          <Button
            variant="primary_s"
            onClick={() => setShowAddPayment(true)}
          >
            <PlusIcon className="w-4 h-4 mr-2" />
            {texts?.cta_add || "Add Payment Method"}
          </Button>
        </div>
      ) : (
        <div className="grid gap-4">
          {paymentMethods.map((method) => (
            <PaymentMethodCard
              key={method.id}
              method={method}
              defaultPaymentMethodId={defaultPaymentMethodId}
              onSetDefault={handleSetDefault}
              onDelete={handleDelete}
              texts={texts}
            />
          ))}
        </div>
      )}

      <LoadingOverlay
        title = {texts?.loading_title || "Please wait..."}
        message={texts?.loading_message || "Processing your request"}
        loading={overlayLoading}
      />

      <AddPaymentMethod
        isOpen={showAddPayment}
        paymentMethods={paymentMethods}
        onClose={() => setShowAddPayment(false)}
      />
    </div>
  )
}

export default UserPaymentMethods